import lightFormat from "date-fns/lightFormat";
import getYear from "date-fns/getYear";
import differenceInYears from "date-fns/differenceInYears";
import { NativeSelect, palette } from "lib/miolo";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import type { IDividendsPaidDetails } from "types/graphqlTypes";
import ClipLoader from "react-spinners/ClipLoader";
import Table from "components/ui/Table";
import { createColumnHelper } from "@tanstack/react-table";
import { TableBreakpoint } from "components/ui/Table/interface";

interface IDividendsPaidTabContentProps {
  dividendsPaid: IDividendsPaidDetails[];
  availableYears: number[];
  loading: boolean;
  filterDividendsByYear: number | null;
  setFilterDividendsByYear: (year: number | null) => void;
}

export default function DividendsPaidTabContent({
  dividendsPaid,
  availableYears,
  loading,
  filterDividendsByYear,
  setFilterDividendsByYear,
}: IDividendsPaidTabContentProps) {
  const columnHelper = createColumnHelper<IDividendsPaidDetails>();

  const arrayOfColumns = [
    columnHelper.accessor("tickerSymbol", {
      header: "Ação",
    }),
    columnHelper.accessor("dividendType", {
      header: "Tipo",
      cell: (props) => (props.getValue() === "JUROS" ? "JCP" : "Dividendo"),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
    columnHelper.accessor("recordDate", {
      header: "Data-Com",
      cell: (props) => lightFormat(parseDateYmd(props.getValue()), "dd/MM/yyyy"),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
    columnHelper.accessor("excludingDate", {
      header: "Data-Ex",
      cell: (props) => getExcludingDate(parseDateYmd(props.row.original.recordDate), parseDateYmd(props.getValue())),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
    columnHelper.accessor("payDate", {
      header: "Pagamento",
      cell: (props) => (props.getValue() ? getPayDate(parseDateYmd(props.getValue())) : "-"),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
    columnHelper.accessor("amountPaid", {
      header: "Valor (R$)",
      cell: (props) => numeral(props.getValue()).format("0.00").replace(".", ","),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
  ];

  return (
    <div className="flex flex-col items-end space-y-2">
      <div className="w-full md:w-52">
        <NativeSelect
          height={48}
          value={filterDividendsByYear ?? ""}
          onChange={(event) => {
            setFilterDividendsByYear(event.target.value ? parseInt(event.target.value, 10) : null);
          }}
          style={{
            borderRadius: "8px",
          }}
        >
          <option value="">Filtre Por Ano</option>
          {availableYears.map((year) => (
            <option key={`selectBoxYear/${year}`} value={year}>
              {year}
            </option>
          ))}
        </NativeSelect>
      </div>

      {loading && (
        <div className="flex w-full items-center justify-center">
          <ClipLoader size={28} color={palette.royalBlue} loading={true} />
        </div>
      )}

      {!loading && (
        <Table<IDividendsPaidDetails>
          columns={arrayOfColumns}
          data={dividendsPaid ?? []}
          isOddEven={true}
          isPaginationEnabled={true}
        />
      )}
    </div>
  );
}

function getExcludingDate(recordDate: Date, excludingDate: Date) {
  const difference = differenceInYears(excludingDate, recordDate);

  if (difference >= 2) {
    return "-";
  }

  return lightFormat(excludingDate, "dd/MM/yyyy");
}

function getPayDate(payDate: Date) {
  if (getYear(payDate) === 1900) {
    return "-";
  }

  return lightFormat(payDate, "dd/MM/yyyy");
}
